import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { BACKEND_URL, timeToMins, fmt, calcPricing, localISO } from '../utils';
import { CheckoutModal } from './CheckoutModal';
import { VipCheckoutModal } from './booking/VipCheckoutModal';
import { TermsModal } from './TermsModal';
import { useLang, LanguageToggle } from '../i18n';
import { CalendarGrid } from './booking/CalendarGrid';
import { SlotList } from './booking/SlotList';
import { buildCalCells } from './booking/buildCalCells';

const pickLang = (obj, lang) => (obj && (obj[lang] || obj.en)) || '';

export function BookingCalendar({ config, bookings, onBook, onGoAdmin, onGoStaff, onConfigUpdate, isShareable, trackEvent }) {
  const { t, lang, monthNames, dayNames } = useLang();
  const today = new Date();
  const todayISO = config.todayISO || localISO();

  const [calMonth, setCalMonth] = useState(() => today.getMonth());
  const [calYear, setCalYear] = useState(() => today.getFullYear());

  // Day-pass state
  const [selectedDate, setSelectedDate] = useState(() => todayISO);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [guests, setGuests] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [availability, setAvailability] = useState({});
  const [urgencyConfig, setUrgencyConfig] = useState({});
  const [preselectedBundleId, setPreselectedBundleId] = useState(null);
  const [pendingBundleId, setPendingBundleId] = useState(null);

  // VIP Day Pass state (LR-124/125) — slot-less full-day paid pass
  const [vipInfo, setVipInfo] = useState(null);
  const [showVipCheckout, setShowVipCheckout] = useState(false);

  const [showFooterTerms, setShowFooterTerms] = useState(false);

  const specialHourTypesById = useMemo(() => Object.fromEntries(
    (config.specialHourTypes || []).map(ty => [ty.id, ty])
  ), [config.specialHourTypes]);

  const fetchAvailability = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/availability`);
      if (res.ok) {
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { return; }
        setAvailability(data.slots || data);
        if (data.urgencyConfig) setUrgencyConfig(data.urgencyConfig);
      }
    } catch (e) {}
  }, []);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);
  useEffect(() => { const id = setInterval(fetchAvailability, 15000); return () => clearInterval(id); }, [fetchAvailability]);

  const fetchVipInfo = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/vip/info`);
      if (res.ok) {
        const data = await res.json();
        setVipInfo(data && data.enabled ? data : null);
      }
    } catch (e) {}
  }, []);
  useEffect(() => { fetchVipInfo(); }, [fetchVipInfo]);
  useEffect(() => { const id = setInterval(fetchVipInfo, 15000); return () => clearInterval(id); }, [fetchVipInfo]);

  const vipDay = useMemo(
    () => (vipInfo?.days || []).find(d => d.date === selectedDate) || null,
    [vipInfo, selectedDate]
  );

  const slotsByDate = useMemo(() => {
    const map = {};
    if (config.slots) {
      config.slots.forEach(s => {
        if (!map[s._iso]) map[s._iso] = [];
        map[s._iso].push(s);
      });
      Object.values(map).forEach(arr => arr.sort((a, b) => timeToMins(a.time) - timeToMins(b.time)));
    }
    return map;
  }, [config.slots]);

  const nowMins = config.nowMinsAST != null ? config.nowMinsAST : (today.getHours() * 60 + today.getMinutes());
  const selectedDaySlots = useMemo(() => (slotsByDate[selectedDate] || []).filter(slot => {
    if (selectedDate > todayISO) return true;
    if (selectedDate < todayISO) return false;
    return timeToMins(slot.time) > nowMins;
  }), [slotsByDate, selectedDate, todayISO, nowMins]);

  const selectedDateObj = new Date(selectedDate + 'T12:00:00');
  const selectedDayName = dayNames[selectedDateObj.getDay()];

  const calDays = useMemo(() => {
    const firstDow = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDow; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [calYear, calMonth]);

  const canGoBack = calYear > today.getFullYear() || (calYear === today.getFullYear() && calMonth > today.getMonth());
  const maxDate = new Date(today); maxDate.setDate(today.getDate() + (Number(config?.customerBookingWindowDays) > 0 ? Number(config.customerBookingWindowDays) : 30));
  const canGoForward = calYear < maxDate.getFullYear() || (calYear === maxDate.getFullYear() && calMonth < maxDate.getMonth());

  const goPrevMonth = useCallback(() => setCalMonth(m => {
    if (m === 0) { setCalYear(y => y - 1); return 11; }
    return m - 1;
  }), []);
  const goNextMonth = useCallback(() => setCalMonth(m => {
    if (m === 11) { setCalYear(y => y + 1); return 0; }
    return m + 1;
  }), []);

  const dayPassCells = useMemo(() => buildCalCells(calDays, calYear, calMonth, todayISO, selectedDate, slotsByDate,
    (iso) => { setSelectedDate(iso); setSelectedSlotId(null); setGuests(1); }
  ), [calDays, calYear, calMonth, todayISO, selectedDate, slotsByDate]);

  const selectedSlot = useMemo(() => config.slots.find(s => s.id === selectedSlotId), [config.slots, selectedSlotId]);
  const slotAvail = selectedSlot && availability[selectedSlot.id] ? availability[selectedSlot.id] : null;
  const displayRemaining = slotAvail ? slotAvail.displayRemaining : (selectedSlot ? selectedSlot.capacity : 0);
  const slotIsPopular = slotAvail ? slotAvail.isPopular : false;
  const pricing = useMemo(() => selectedSlot ? calcPricing(selectedSlot.price, guests, config.taxRate, config.serviceFeeRate) : null, [selectedSlot, guests, config.taxRate, config.serviceFeeRate]);

  const handleCheckoutComplete = useCallback(async (booking) => {
    await onBook(booking);
    await fetchAvailability();
  }, [onBook, fetchAvailability]);

  const handleCheckoutClose = useCallback(() => {
    setShowCheckout(false);
    setSelectedSlotId(null);
    setGuests(1);
    setPreselectedBundleId(null);
    setPendingBundleId(null);
  }, []);

  const handleVipComplete = useCallback(async () => {
    // VIP is slot-less: only its own daily availability changes. Refresh both so the
    // sold-out state and day-pass counts stay current.
    await fetchVipInfo();
    await fetchAvailability();
  }, [fetchVipInfo, fetchAvailability]);

  const slotsRef = useRef(null);
  const activeBundles = (config.bundles || []).filter(b => b.active && b.passCount > 0);
  const featuredBundles = activeBundles.filter(b => b.featured);

  const handleTakeOffer = useCallback((bundle) => {
    if (selectedSlotId) {
      setGuests(bundle.passCount);
      setPreselectedBundleId(bundle.id);
      setPendingBundleId(null);
      setShowCheckout(true);
    } else {
      setPendingBundleId(bundle.id);
      setPreselectedBundleId(null);
      slotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedSlotId]);

  // Height-sync: the hours panel's bottom edge matches the calendar group's
  // bottom edge, but only while the two columns sit side-by-side (not stacked
  // on narrow viewports). Comparing the columns themselves (not the calendar
  // group vs. the panel) keeps this correct even when extra content — like the
  // promo banners — sits above the panel in the right column.
  const leftColRef = useRef(null);
  const rightColRef = useRef(null);
  const calGroupRef = useRef(null);
  const hoursPanelRef = useRef(null);
  useLayoutEffect(() => {
    const sync = () => {
      const leftCol = leftColRef.current, rightCol = rightColRef.current;
      const g = calGroupRef.current, p = hoursPanelRef.current;
      if (!leftCol || !rightCol || !g || !p) return;
      const sideBySide = Math.abs(leftCol.getBoundingClientRect().top - rightCol.getBoundingClientRect().top) < 24;
      if (!sideBySide) { p.style.height = ''; return; }
      const targetHeight = g.getBoundingClientRect().bottom - p.getBoundingClientRect().top;
      p.style.height = targetHeight > 0 ? targetHeight + 'px' : '';
    };
    sync();
    const ro = new ResizeObserver(sync);
    if (leftColRef.current) ro.observe(leftColRef.current);
    window.addEventListener('resize', sync);
    return () => { ro.disconnect(); window.removeEventListener('resize', sync); };
  }, [calMonth, calYear, selectedDate, selectedSlotId, selectedDaySlots.length, featuredBundles.length, vipInfo, vipDay]);

  const dayPassSlotItems = useMemo(() => selectedDaySlots.map(slot => {
    const sa = availability[slot.id];
    const realRem = sa ? slot.capacity - sa.booked : slot.capacity;
    const dispRem = sa ? sa.displayRemaining : slot.capacity;
    const isPopular = sa ? sa.isPopular : false;
    const isSoldOut = realRem <= 0;
    const isSelected = selectedSlotId === slot.id;

    const lowThresh = urgencyConfig.urgencyLowThreshold ?? 4;
    const critThresh = urgencyConfig.urgencyCriticalThreshold ?? 2;
    const showUrgency = urgencyConfig.urgencyEnabled !== false;
    const showPopular = urgencyConfig.popularEnabled !== false;

    let urgencyPill = null;
    if (isSoldOut) urgencyPill = { kind: 'sold-out-pill', label: t('soldOut') };
    else if (showUrgency && dispRem === 1) urgencyPill = { kind: 'urgent-red pulse', label: t('lastSpot') };
    else if (showUrgency && dispRem <= critThresh) urgencyPill = { kind: 'urgent-red pulse', label: `${dispRem} ${t('left')}` };
    else if (showUrgency && dispRem <= lowThresh) urgencyPill = { kind: 'urgent-amber', label: `${dispRem} ${t('left')}` };

    return {
      id: slot.id, time: slot.time, priceLabel: fmt(slot.price),
      isSelected, isSoldOut, urgencyPill,
      popularLabel: (!isSoldOut && showPopular && isPopular) ? t('popular') : null,
      specialHours: Array.isArray(slot.specialHours)
        ? slot.specialHours.map((sh, i) => ({ special: sh, type: specialHourTypesById[sh.typeId], key: sh.ruleId || sh.typeId || i })).filter(x => x.type)
        : [],
      variant: 'daypass',
      onClick: () => {
        if (isSelected) { setSelectedSlotId(null); setGuests(1); }
        else {
          setSelectedSlotId(slot.id);
          if (pendingBundleId) {
            const pb = activeBundles.find(b => b.id === pendingBundleId);
            if (pb) { setGuests(pb.passCount); setPreselectedBundleId(pb.id); }
            setPendingBundleId(null);
          } else {
            setGuests(1);
          }
          if (trackEvent) trackEvent('add_to_cart', { value: slot.price });
        }
      },
    };
  }), [selectedDaySlots, availability, selectedSlotId, urgencyConfig, specialHourTypesById, pendingBundleId, activeBundles, trackEvent, t]);

  const hasSel = !!selectedSlot;

  return (
    <div data-testid="booking-page">
      <div className="lr-hdr" data-testid="main-header">
        <a href="https://www.lasruinas.com/" target="_blank" rel="noopener noreferrer" className="lr-hdr-logo">
          <img src="/LAS_RUINAS_LOGO_BEIGE.png" alt="Las Ruinas" />
        </a>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <LanguageToggle />
          {!isShareable && (
            <>
              <button className="admin-btn" data-testid="staff-button" onClick={onGoStaff}>Staff</button>
              <button className="admin-btn" data-testid="admin-button" onClick={onGoAdmin}>Admin</button>
            </>
          )}
        </div>
      </div>

      <div data-testid="book-tab">
        <div className="lr-hero">
          <h2>{t('reserveYour')}<br/><em>{t('dayPass')}</em></h2>
          <p>{t('heroSubtitle')}</p>
        </div>
        <div data-testid="day-pass-description" style={{background:'#F5F0E8',padding:'20px 24px',borderLeft:'3px solid #C8A96E',borderBottom:'0.5px solid #E8E2D8'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:8}}>
            <span style={{color:'#1C1814',fontSize:15,fontWeight:500}}>{pickLang(config.siteContent?.dayPass?.name, lang) || t('dayPassLabel')}</span>
          </div>
          <p style={{color:'#4A4438',fontSize:13,lineHeight:1.6,margin:'0 0 10px'}}>{pickLang(config.siteContent?.dayPass?.description, lang) || t('dayPassDesc')}</p>
          <p style={{color:'#8A7E6E',fontSize:12,margin:'0 0 10px',fontStyle:'italic'}}>{pickLang(config.siteContent?.dayPass?.subdescription, lang) || t('dayPassNote')}</p>
          <a href="/tours" className="lr-terms-link" data-testid="cross-link-tours">{t('lookingForTourLink')}</a>
        </div>

        <div className="lr-book-cal">
          <div className="lr-book-cols">
            <div ref={leftColRef} className="lr-book-left">
              <div ref={calGroupRef} style={{display:'flex',flexDirection:'column',gap:18}}>
                <div className="mc-date-header" data-testid="date-header">
                  <div className="mc-dayname">{selectedDayName}</div>
                  <div className="mc-datestr">{monthNames[selectedDateObj.getMonth()].toUpperCase()} {selectedDateObj.getDate()}</div>
                </div>
                <div className="lr-cal-card">
                  <CalendarGrid
                    monthLabel={`${monthNames[calMonth]} ${calYear}`}
                    cells={dayPassCells}
                    canGoBack={canGoBack}
                    canGoForward={canGoForward}
                    onPrev={goPrevMonth}
                    onNext={goNextMonth}
                  />
                </div>
                {vipInfo && (
                  <div className="lr-bundle-upsell" data-testid="vip-upsell-card" style={{
                    background: '#F5F0E8', border: '1px solid #C8A96E', borderRadius: 8,
                    padding: '14px 16px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--bark,#1C1814)' }}>{pickLang(config.siteContent?.vip?.name, lang) || vipInfo.name}</div>
                      {vipDay && !vipDay.soldOut && (
                        <div data-testid="vip-price" style={{ fontSize: 14, fontWeight: 600, color: 'var(--bark,#1C1814)', whiteSpace: 'nowrap' }}>{fmt(vipDay.price)}</div>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#4A4438', lineHeight: 1.6, marginBottom: 6 }}>{pickLang(config.siteContent?.vip?.description, lang) || t('vipCardDesc')}</div>
                    {(() => {
                      const _vsub = pickLang(config.siteContent?.vip?.subdescription, lang);
                      return _vsub ? (
                        <div style={{ fontSize: 12, color: '#8A7E6E', fontStyle: 'italic', lineHeight: 1.6, marginBottom: 6 }}>{_vsub}</div>
                      ) : null;
                    })()}
                    {vipDay && !vipDay.soldOut && (
                      <div data-testid="vip-access-window" style={{ fontSize: 12, color: '#8A7E6E', marginBottom: 10 }}>
                        {t('vipAccessWindow', { window: vipDay.accessWindow })}
                      </div>
                    )}
                    {vipDay && !vipDay.soldOut ? (
                      <button
                        className="lr-btn-sm lr-btn-accent"
                        data-testid="reserve-vip-button"
                        style={{ width: '100%' }}
                        onClick={() => { setShowVipCheckout(true); if (trackEvent) trackEvent('begin_checkout', { value: vipDay.price, product: 'vip' }); }}
                      >
                        {t('vipReserveCta')}
                      </button>
                    ) : (
                      <div data-testid="vip-sold-out" style={{ fontSize: 12, color: '#8B7E74', fontStyle: 'italic', textAlign: 'center', padding: '6px 0' }}>
                        {t('vipSoldOutDate')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div ref={rightColRef} className="lr-book-right">
              {featuredBundles.map(bundle => (
                <div key={bundle.id} className="lr-bundle-upsell" data-testid={`bundle-upsell-${bundle.id}`} style={{
                  background: '#FFF8E1', border: '1px solid #F0D98C', borderRadius: 8,
                  padding: '12px 16px', marginBottom: 12,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                }}>
                  <div>
                    <div style={{fontSize:13, fontWeight:600, color:'var(--bark,#1C1814)'}}>
                      {t('bundleUpsellTitle', { n: bundle.passCount, pct: bundle.discountPercent })}
                    </div>
                    <div style={{fontSize:11, color:'#8B7E74', marginTop:2}}>{t('bundleUpsellDesc')}</div>
                  </div>
                  <button className="lr-btn-sm lr-btn-accent" data-testid="take-bundle-offer" style={{whiteSpace:'nowrap'}} onClick={() => handleTakeOffer(bundle)}>
                    {t('takeOffer')}
                  </button>
                </div>
              ))}
              <div ref={hoursPanelRef} className="lr-hours-panel">
                <div className="lr-hours-title">{t('availableTimes')}</div>
                <div className="lr-hours-scroll">
                  <div data-testid="slots-list" ref={slotsRef}>
                    {pendingBundleId && (() => {
                      const pb = activeBundles.find(b => b.id === pendingBundleId);
                      return pb ? (
                        <div data-testid="bundle-pending-hint" style={{
                          background: '#FFF8E1', border: '1px solid #F0D98C', borderRadius: 8,
                          padding: '10px 14px', marginBottom: 10, fontSize: 12,
                          color: 'var(--bark)', display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          <span style={{flex:1}}>{t('selectTimeForBundle')}</span>
                          <button style={{fontSize:13, color:'#8B7E74', background:'none', border:'none', cursor:'pointer', lineHeight:1}} onClick={() => setPendingBundleId(null)}>&times;</button>
                        </div>
                      ) : null;
                    })()}
                    <SlotList slots={dayPassSlotItems} emptyLabel={t('noSlots')} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`lr-book-bar${hasSel && pricing ? ' pinned' : ''}`} data-testid="checkout-bar">
            {hasSel && pricing ? (
              <>
                <div className="bb-info">
                  <div className="bb-line1">{selectedSlot.day}, {selectedSlot.date} &middot; {selectedSlot.time}</div>
                  <div className="bb-line2">{guests} {guests > 1 ? t('guests') : t('guest')} &middot; {fmt(pricing.total)}</div>
                </div>
                <div className="ck-guests">
                  <div className="g-label">{t('guestsLabel')}</div>
                  <button data-testid="guests-minus" disabled={guests <= 1 || showCheckout} onClick={() => setGuests(g => g - 1)}>&minus;</button>
                  <span className="g-count">{guests}</span>
                  <button data-testid="guests-plus" disabled={guests >= displayRemaining || showCheckout} onClick={() => setGuests(g => g + 1)}>+</button>
                  {(() => {
                    const best = (config.bundles || [])
                      .filter(b => b.active && b.passCount <= guests)
                      .sort((a, b) => b.discountPercent - a.discountPercent || b.passCount - a.passCount)[0];
                    return best ? (
                      <span className="ck-bundle-hint" data-testid="bundle-auto-hint">
                        {best.discountPercent}% bundle deal
                      </span>
                    ) : null;
                  })()}
                </div>
              </>
            ) : (
              <div className="bb-placeholder">{t('selectTimeToContinue')}</div>
            )}
            <button className={`lr-cta${hasSel ? '' : ' is-disabled'}`} data-testid="reserve-button" disabled={!hasSel}
              onClick={() => { setShowCheckout(true); if (trackEvent) trackEvent('begin_checkout', { value: pricing.total }); }}>
              {hasSel && pricing ? <>{t('reserve')} &middot; {fmt(pricing.total)}</> : t('reserve')}
            </button>
          </div>
        </div>
      </div>

      {showCheckout && selectedSlot && (
        <CheckoutModal
          slot={selectedSlot}
          guests={guests}
          config={config}
          displayRemaining={displayRemaining}
          isPopular={slotIsPopular}
          urgencyConfig={urgencyConfig}
          onClose={handleCheckoutClose}
          onComplete={handleCheckoutComplete}
          trackEvent={trackEvent}
          preselectedBundleId={preselectedBundleId}
          onGuestsChange={setGuests}
        />
      )}

      {showVipCheckout && vipInfo && vipDay && !vipDay.soldOut && (
        <VipCheckoutModal
          vipInfo={vipInfo}
          date={selectedDate}
          dayInfo={vipDay}
          config={config}
          onClose={() => setShowVipCheckout(false)}
          onComplete={handleVipComplete}
          trackEvent={trackEvent}
        />
      )}

      {/* Footer */}
      <footer className="lr-footer" data-testid="booking-footer">
        <div className="lr-footer-inner">
          <div className="lr-footer-copy">{t('footerCopy')}</div>
          <div className="lr-footer-links">
            <a href="/privacy" data-testid="footer-privacy-link">{t('privacyPolicy')}</a>
            <span className="lr-footer-dot">&middot;</span>
            <button type="button" className="lr-footer-link-btn" data-testid="footer-terms-link" onClick={() => setShowFooterTerms(true)}>{t('termsAndConditions')}</button>
            <span className="lr-footer-dot">&middot;</span>
            <a href="mailto:hello@lasruinas.com" data-testid="footer-email-link">hello@lasruinas.com</a>
          </div>
          <div className="lr-footer-stripe">
            <a href="https://stripe.com/docs/security" target="_blank" rel="noopener noreferrer" data-testid="footer-stripe-link">{t('paymentsSecuredBy')} &#128274;</a>
          </div>
        </div>
      </footer>
      {showFooterTerms && (
        <TermsModal
          onAgree={() => setShowFooterTerms(false)}
          onClose={() => setShowFooterTerms(false)}
        />
      )}
    </div>
  );
}
